"use client";

import ArrowClockwise from "@/assets/vectors/ArrowClockwise";
import BackwardFilled from "@/assets/vectors/BackwardFilled";
import ForwardFilled from "@/assets/vectors/ForwardFilled";
import Pause from "@/assets/vectors/Pause";
import Play from "@/assets/vectors/Play";
import RectangleExpandDiagonal from "@/assets/vectors/RectangleExpandDiagonal";
import AnimatedText from "@/components/animated_text/AnimatedText";
import Button from "@/components/button/Button";
import ProgressBar from "@/components/progress_bar/ProgressBar";
import { useEffect, useRef, useState } from "react";
import { videoSources, videoPoster } from "@/lib/media";
import styles from "./video_player.module.scss";

function formatTime(timeInSeconds: number): string {
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

type VideoPlayerProps = {
    src: string;
    width?: number;
    height?: number;
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    onReady?: () => void;
    onError?: () => void;
};

function getEffectiveDuration(video: HTMLVideoElement): number {
    const rawDuration = video.duration
    if (Number.isFinite(rawDuration) && rawDuration > 0) return rawDuration

    // Some servers/encodes (e.g., MP4 with moov atom at end) may not expose a finite
    // duration early, but seekable/buffered ranges still advance as data arrives.
    const seekable = video.seekable
    if (seekable && seekable.length > 0) {
        const end = seekable.end(seekable.length - 1)
        if (Number.isFinite(end) && end > 0) return end
    }

    const buffered = video.buffered
    if (buffered && buffered.length > 0) {
        const end = buffered.end(buffered.length - 1)
        if (Number.isFinite(end) && end > 0) return end
    }

    return 0
}

function getTimelineWindow(video: HTMLVideoElement): { start: number; end: number } {
    const rawDuration = video.duration
    if (Number.isFinite(rawDuration) && rawDuration > 0) {
        return { start: 0, end: rawDuration }
    }

    const seekable = video.seekable
    if (seekable && seekable.length > 0) {
        const start = seekable.start(seekable.length - 1)
        const end = seekable.end(seekable.length - 1)
        if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
            return { start, end }
        }
    }

    const buffered = video.buffered
    if (buffered && buffered.length > 0) {
        const end = buffered.end(buffered.length - 1)
        if (Number.isFinite(end) && end > 0) {
            return { start: 0, end }
        }
    }

    return { start: 0, end: 0 }
}

export default function VideoPlayer({ src, width = 1920, height = 1080, autoplay, muted, loop, onReady, onError }: VideoPlayerProps) {
    const [duration, setDuration] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [status, setStatus] = useState<'play' | 'pause'>(autoplay ? 'play' : 'pause');
    const [controllerVisibility, setControllerVisibility] = useState(true)
    const [hasEnded, setHasEnded] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null);
    const controllerRef = useRef<HTMLDivElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null);
    const [loadMedia, setLoadMedia] = useState(false);
    useEffect(() => {
        if (loadMedia) videoRef.current?.load();
    }, [loadMedia, src]);
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setLoadMedia(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });
        observer.observe(video);
        return () => observer.disconnect();
    }, [src]);
    const controllerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const rafRef = useRef<number | null>(null)
    const syncIntervalRef = useRef<number | null>(null)
    const timelineStartRef = useRef<number>(0)
    const readyFiredRef = useRef(false)

    const fireReadyOnce = () => {
        if (readyFiredRef.current) return
        readyFiredRef.current = true
        onReady?.()
    }

    const scheduleControllerHide = (delayMs: number) => {
        if (controllerTimeoutRef.current) {
            clearTimeout(controllerTimeoutRef.current)
        }
        if (status !== 'play') return
        controllerTimeoutRef.current = setTimeout(() => {
            setControllerVisibility(false)
        }, delayMs)
    }

    const getFullscreenElement = (): Element | null => {
        return (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? null) as Element | null
    }

    const getIsFullscreen = () => {
        const video = videoRef.current
        const videoFullscreen = Boolean((video as any)?.webkitDisplayingFullscreen)
        return Boolean(getFullscreenElement()) || videoFullscreen
    }

    const toggleFullScreen = async () => {
        try {
            const containerEl = containerRef.current
            const video = videoRef.current

            const isCurrentlyFullscreen = getIsFullscreen()

            if (!isCurrentlyFullscreen) {
                // Prefer standards-based fullscreen on the container when available.
                if (containerEl && (containerEl as any).requestFullscreen) {
                    await (containerEl as any).requestFullscreen()
                    return
                }
                // Safari (macOS) prefixed fullscreen for elements.
                if (containerEl && (containerEl as any).webkitRequestFullscreen) {
                    await (containerEl as any).webkitRequestFullscreen()
                    return
                }
                // iOS Safari: only the <video> element can enter fullscreen.
                if (video && (video as any).webkitEnterFullscreen) {
                    ;(video as any).webkitEnterFullscreen()
                    return
                }
                return
            }

            // Exit fullscreen
            if (getFullscreenElement()) {
                if ((document as any).exitFullscreen) {
                    await (document as any).exitFullscreen()
                } else if ((document as any).webkitExitFullscreen) {
                    await (document as any).webkitExitFullscreen()
                }
                return
            }

            if (video && (video as any).webkitDisplayingFullscreen) {
                if ((video as any).webkitExitFullscreen) {
                    ;(video as any).webkitExitFullscreen()
                } else if ((document as any).webkitExitFullscreen) {
                    await (document as any).webkitExitFullscreen()
                }
            }
        } catch {
            // ignore failures
        }
    }

    const handleDoubleClick = () => {
        toggleFullScreen()
    }

    const syncFromVideo = () => {
        const video = videoRef.current
        if (!video) return

        const nextStatus: 'play' | 'pause' = video.paused ? 'pause' : 'play'
        setStatus(prev => (prev === nextStatus ? prev : nextStatus))

        // Keep ended state in sync with the actual media element.
        // This covers cases where the `ended` event fired before hydration.
        if (!loop) {
            setHasEnded(video.ended)
        }

        const window = getTimelineWindow(video)
        timelineStartRef.current = window.start

        const effectiveDuration = Math.max(0, window.end - window.start)
        setDuration(effectiveDuration)

        // Consider the player "ready" once we have any meaningful timeline.
        if (effectiveDuration > 0 || (video.readyState ?? 0) >= 2) {
            fireReadyOnce()
        }

        if (Number.isFinite(video.currentTime)) {
            const relativeTime = Math.max(0, video.currentTime - window.start)
            setCurrentTime(relativeTime)

            if (effectiveDuration > 0 && Math.abs(video.currentTime - window.end) < 0.1) {
                setHasEnded(true)
            }
        }
    }

    const startRafSync = () => {
        if (rafRef.current !== null) return

        const tick = () => {
            syncFromVideo()
            rafRef.current = window.requestAnimationFrame(tick)
        }

        rafRef.current = window.requestAnimationFrame(tick)
    }

    const stopRafSync = () => {
        if (rafRef.current === null) return
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
    }

    const startSyncLoop = () => {
        if (syncIntervalRef.current !== null) return
        // Use a small interval to guarantee updates even if media events are sparse
        // or happened before hydration (common with autoplay).
        syncIntervalRef.current = window.setInterval(() => {
            syncFromVideo()
        }, 250)
    }

    const stopSyncLoop = () => {
        if (syncIntervalRef.current === null) return
        window.clearInterval(syncIntervalRef.current)
        syncIntervalRef.current = null
    }

    useEffect(() => {
        // Drive syncing from React state so we don't rely on media events
        // (which can fire before hydration when autoplaying).
        if (status === 'play') {
            startRafSync()
            startSyncLoop()
        } else {
            stopSyncLoop()
            stopRafSync()
        }

        return () => {
            stopSyncLoop()
            stopRafSync()
        }
    }, [status])

    // Sync fullscreen state when user exits/enters with ESC or browser controls
    useEffect(() => {
        const onFs = () => {
            setIsFullscreen(getIsFullscreen())
        }

        document.addEventListener('fullscreenchange', onFs)
        // vendor prefixed
        document.addEventListener('webkitfullscreenchange', onFs as any)

        // iOS Safari: fullscreen events on the video element
        const video = videoRef.current
        video?.addEventListener('webkitbeginfullscreen', onFs as any)
        video?.addEventListener('webkitendfullscreen', onFs as any)

        // initial sync
        onFs()

        return () => {
            document.removeEventListener('fullscreenchange', onFs)
            document.removeEventListener('webkitfullscreenchange', onFs as any)
            video?.removeEventListener('webkitbeginfullscreen', onFs as any)
            video?.removeEventListener('webkitendfullscreen', onFs as any)
        }
    }, [])

    useEffect(() => {
        // Ensure state is correct on mount/hydration and when loop changes.
        // Also run a short bootstrap poll to capture duration/time if the media events
        // (e.g., loadedmetadata/play) occurred before hydration.
        syncFromVideo()

        const bootstrapStart = Date.now()
        const bootstrap = window.setInterval(() => {
            syncFromVideo()
            const videoEl = videoRef.current
            const effectiveDuration = videoEl ? getEffectiveDuration(videoEl) : 0
            const hasDuration = effectiveDuration > 0
            const timeIsMoving = videoEl ? videoEl.currentTime > 0 : false
            const timedOut = Date.now() - bootstrapStart > 5000
            if (hasDuration || timeIsMoving || timedOut) {
                window.clearInterval(bootstrap)
            }
        }, 200)

        return () => {
            window.clearInterval(bootstrap)
            // `status` effect owns sync loop lifecycle.
        }
    }, [loop])

    // NOTE: we sync time/duration via DOM events + rAF to support streaming/live media

    const handleReplay = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            setCurrentTime(0);
            setHasEnded(false);
            videoRef.current.play();
            setStatus('play');
        }
    };

    const handlePlayPause = () => {
        if (videoRef.current) {
            // If video has ended, replay from start
            if (hasEnded) {
                handleReplay();
            } else if (videoRef.current.paused) {
                videoRef.current.play();
                setStatus('play');
            } else {
                videoRef.current.pause();
                setStatus('pause');
            }
        }
    };

    const handleSeek = (time: number) => {
        if (videoRef.current) {
            const wasPlaying = !videoRef.current.paused;

            const absoluteTime = time + timelineStartRef.current

            videoRef.current.currentTime = absoluteTime;
            setCurrentTime(time);

            const effectiveDuration = getEffectiveDuration(videoRef.current)

            // Reset ended state if seeking away from end
            if (hasEnded && effectiveDuration > 0 && time < effectiveDuration - 0.5) {
                setHasEnded(false);
                // Auto-play after seeking if video had ended
                if (videoRef.current.paused) {
                    videoRef.current.play();
                    setStatus('play');
                }
            } else if (wasPlaying && videoRef.current.paused) {
                // Continue playing if it was playing before seek
                videoRef.current.play();
            }
        }
    };

    const remainingTime = duration > 0 ? Math.max(0, duration - currentTime) : 0

    return (
        <div
            className={styles.container}
            ref={containerRef}
            onDoubleClick={() => handleDoubleClick()}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'f' || e.key === 'F') {
                    e.preventDefault()
                    toggleFullScreen()
                }
            }}
            onMouseEnter={() => {
                setControllerVisibility(true)
                if (controllerTimeoutRef.current) clearTimeout(controllerTimeoutRef.current)
            }}
            onMouseLeave={() => {
                scheduleControllerHide(800)
            }}
            onPointerDown={() => {
                setControllerVisibility(true)
                if (controllerTimeoutRef.current) clearTimeout(controllerTimeoutRef.current)
                scheduleControllerHide(1000)
            }}
            onPointerMove={() => {
                if (!controllerVisibility) setControllerVisibility(true)
                if (controllerTimeoutRef.current) clearTimeout(controllerTimeoutRef.current)
                scheduleControllerHide(1000)
            }}
            onFocusCapture={() => {
                setControllerVisibility(true)
                if (controllerTimeoutRef.current) clearTimeout(controllerTimeoutRef.current)
            }}
            onBlurCapture={() => {
                scheduleControllerHide(1200)
            }}
        >
            <video
                className={styles.video}
                ref={videoRef}
                onLoadedMetadata={syncFromVideo}
                onLoadedData={syncFromVideo}
                onCanPlay={() => {
                    syncFromVideo()
                    fireReadyOnce()
                }}
                onCanPlayThrough={() => {
                    syncFromVideo()
                    fireReadyOnce()
                }}
                onDurationChange={syncFromVideo}
                onProgress={syncFromVideo}
                onTimeUpdate={syncFromVideo}
                onSeeking={syncFromVideo}
                onSeeked={syncFromVideo}
                onError={() => {
                    onError?.()
                }}
                onPlay={() => {
                    setHasEnded(false)
                    setStatus('play')
                    startRafSync()
                    startSyncLoop()
                    scheduleControllerHide(800)
                }}
                onPlaying={() => {
                    setHasEnded(false)
                    setStatus('play')
                    startRafSync()
                    startSyncLoop()
                }}
                onPause={() => {
                    setStatus('pause')
                    stopSyncLoop()
                    stopRafSync()
                    syncFromVideo()
                    setControllerVisibility(true)
                }}
                onEnded={() => {
                    stopSyncLoop()
                    stopRafSync()
                    if (!loop) {
                        setHasEnded(true)
                        setStatus('pause')
                        setControllerVisibility(true)
                    }
                }}
                width={width}
                height={height}
                playsInline
                crossOrigin="anonymous"
                poster={videoPoster(src)}
                preload="metadata"
                onClick={() => handlePlayPause()}
                {...(autoplay ? { autoPlay: true } : {})}
                {...(muted ? { muted: true } : {})}
                {...(loop ? { loop: true } : {})}
            >
                {loadMedia && videoSources(src).map(source => <source key={source.src} {...source} />)}
            </video>

            <div className={styles.controller_wrapper}>
                <Button
                    className={`${styles.full_screen_button} ${controllerVisibility ? styles.visible : undefined}`}
                    startIcon={RectangleExpandDiagonal}
                    onClick={toggleFullScreen}
                    ariaLabel={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                    ariaPressed={isFullscreen}
                    title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                />

                <div
                    ref={controllerRef}
                    className={`${styles.controller} ${controllerVisibility ? styles.visible : undefined}`}
                    role="group"
                    aria-label="Video controls"
                >
                    <Button
                        className={styles.button_container}
                        iconClassName={styles.icon}
                        startIcon={BackwardFilled}
                        status={'disabled'}
                        ariaLabel="Previous"
                    />

                    <Button
                        className={styles.button_container}
                        startIcon={hasEnded ? ArrowClockwise : status === 'play' ? Pause : Play}
                        onClick={handlePlayPause}
                        ariaLabel={status === 'play' ? 'Pause' : hasEnded ? 'Replay' : 'Play'}
                        ariaPressed={status === 'play'}
                    />

                    <Button
                        className={styles.button_container}
                        iconClassName={styles.icon}
                        startIcon={ForwardFilled}
                        status={'disabled'}
                        ariaLabel="Next"
                    />

                    <AnimatedText className={styles.time}>{formatTime(currentTime)}</AnimatedText>

                    <ProgressBar
                        duration={duration}
                        currentTime={currentTime}
                        onSeekAction={handleSeek}
                    />

                    <AnimatedText className={styles.time}>
                        -{formatTime(remainingTime)}
                    </AnimatedText>
                </div>
            </div>
        </div>
    )
}
